import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Snackbar, Alert, Skeleton, Chip, alpha } from "@mui/material";
import { VisibilityOutlined as ViewOnlyIcon } from "@mui/icons-material";
import EntityHeader from "../../../components/molecules/EntityHeader";
import ActionButton from "../../../components/atoms/ActionButton";
import SaveStatusIndicator from "../../../components/atoms/SaveStatusIndicator";
import useClinicTheme from "../../../theme/useClinicTheme";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useActiveModules, moduleChecker } from "../../../hooks/useClinicModule";
import { ExaminationProvider } from "./ExaminationContext";
import useExaminationSession from "./useExaminationSession";
import DurationTimer from "./DurationTimer";
import CompletionBanner, { StatusStrip } from "./CompletionBanner";
import CancelDialog from "./CancelDialog";
import {
  readWorkspaceState,
  writeWorkspaceState,
  clearWorkspaceState,
} from "./workspaceStorage";
import {
  PersonalInfoTab,
  AnamnesisTab,
  InvestigationTab,
  TreatmentsTab,
  PrescriptionsTab,
  DietTab,
  MedicationAlarmsTab,
  HydrationTab,
} from "../../../components/organisms/PatientWorkspaceTabs";
import { COLORS, CARD_PAGE_PAD } from "../../../styles/uiConstants";
import { staffFullName, staffRoleName } from "../../../utils/staffName";
import { formatPhoneDisplay } from "../../../components/atoms/PhoneInput";
import useTerms from "../../../hooks/useTerms";
import { clinical } from "../../../terms";

// Hasta Detay ile aynı düzen: üst şerit (EntityHeader) + altında sekme satırı.
// Tab anahtarları (key) İngilizce; localStorage'daki son sekme bunlarla saklanır.
// `module`: kliniğin modülü kapalıysa sekme gizlenir (Hasta Detay ile aynı kural).
const TABS = [
  { key: "personal-info", label: "Kişisel Bilgiler" },
  { key: "anamnesis", label: "Anamnez", perm: "anamnez:read", module: "anamnesis" },
  { key: "examinations", label: "Tetkik", perm: "tetkik:read", module: "investigation" },
  { key: "treatments", label: "Tedaviler", perm: "treatments:read", module: "treatment" },
  { key: "prescriptions", label: "Reçeteler", perm: "treatments:read", module: "prescription" },
  { key: "diet", label: "Beslenme Planı", perm: "diet:read", module: "diet" },
  { key: "medication-alarms", label: "İlaç Alarmları", perm: "medication-plans:read", module: "medicationAlarms" },
  { key: "hydration", label: "Su Takibi", perm: "hydration:read", module: "hydration" },
];

// Düz (kutusuz) sekmeler: liste | editör — beyaz zemin, kenardan kenara; kendi
// iç hizalarını taşırlar. Diğerleri kartlı: slate50 + CARD_PAGE_PAD (Hasta Detay FLAT_TABS).
const FLAT_TABS = new Set([
  "anamnesis",
  "examinations",
  "treatments",
  "prescriptions",
  "diet",
  "medication-alarms",
]);

const calcAge = (birthDay) => {
  if (!birthDay) return null;
  const d = new Date(birthDay);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

function renderTab(activeTab, common, session, supportData, patient) {
  const examinationId = session?.examination?._id;
  const treatments = Array.isArray(session?.treatments) ? session.treatments : [];
  const activeTreatment =
    treatments.find((t) => t.isActive && t.status === "active") ||
    treatments[0] ||
    null;

  switch (activeTab) {
    case "personal-info":
      // Muayene session'unda SADECE Genel Bilgiler (Ad/Soyad/E-posta/TC vb.)
      // salt okunur olur; Fiziksel Bilgiler / Alerjiler / Kronik Hastalıklar
      // her zaman düzenlenebilir kalır. `initialData` ile besle ki form
      // alanları boş gözükmesin.
      return (
        <PersonalInfoTab
          {...common}
          initialData={patient}
          patientData={patient}
          generalReadOnly
        />
      );

    case "anamnesis":
      return (
        <AnamnesisTab
          {...common}
          initialData={{ list: session?.anamnezList || [], examinationId }}
        />
      );

    case "examinations":
      return (
        <InvestigationTab
          {...common}
          initialData={{
            list: session?.tetkikList || [],
            examinationId,
          }}
        />
      );

    case "treatments":
      return (
        <TreatmentsTab
          {...common}
          initialData={{
            treatments,
            diagnoses: supportData.diagnoses,
            complaints: supportData.complaints,
            medicineTemplates: supportData.medicineTemplates,
            foods: supportData.foods,
            examinationId,
          }}
        />
      );

    case "prescriptions":
      return (
        <PrescriptionsTab
          {...common}
          initialData={{
            treatments,
            medicineTemplates: supportData.medicineTemplates,
            examinationId,
          }}
          activeTreatment={activeTreatment}
        />
      );

    case "medication-alarms":
      return <MedicationAlarmsTab patientId={common.patientId} token={common.token} canEdit={common.canEdit} />;

    case "hydration":
      return (
        <HydrationTab
          patientId={common.patientId}
          token={common.token}
          canManage={common.canManageHydration && common.canEdit}
        />
      );

    case "diet":
      return (
        <DietTab
          {...common}
          initialData={{
            treatments,
            foods: supportData.foods,
            examinationId,
          }}
          activeTreatment={activeTreatment}
        />
      );

    default:
      return null;
  }
}

const fullName = (u, terms = clinical) =>
  u ? `${u.name || ""} ${u.surname || ""}`.trim() || terms.patient : terms.patient;

export default function ExaminationSession() {
  const terms = useTerms();
  const navigate = useNavigate();
  const { palette } = useClinicTheme();
  const vm = useExaminationSession();
  const [activeTab, setActiveTab] = useState("personal-info");
  const [cancelOpen, setCancelOpen] = useState(false);

  // Restore activeTab from localStorage once examinationId is ready.
  const activeTabRestoredRef = useRef(false);
  useEffect(() => {
    if (!vm.examinationId || activeTabRestoredRef.current) return;
    const restored = readWorkspaceState(vm.examinationId)?.activeTab;
    if (restored) setActiveTab(restored);
    activeTabRestoredRef.current = true;
  }, [vm.examinationId]);

  // Persist activeTab to localStorage on change.
  useEffect(() => {
    if (!vm.examinationId) return;
    writeWorkspaceState(vm.examinationId, (prev) => ({
      ...prev,
      activeTab,
    }));
  }, [vm.examinationId, activeTab]);

  // Clear localStorage when muayene is completed or cancelled.
  useEffect(() => {
    if (!vm.examinationId) return;
    const status = vm.session?.examination?.status;
    if (status === "completed" || status === "cancelled") {
      clearWorkspaceState(vm.examinationId);
    }
  }, [vm.examinationId, vm.session?.examination?.status]);

  // Permission + module based tab visibility. Kişisel Bilgiler is always visible
  // (it's the subject of the examination, implicit from examinations:read).
  // useHasPermission already bypasses for Admin.
  const hasModule = moduleChecker(useActiveModules());
  const canAnamnez = useHasPermission("anamnez:read");
  const canTetkik = useHasPermission("tetkik:read");
  const canTreatments = useHasPermission("treatments:read");
  const canDiet = useHasPermission("diet:read");
  const canMedPlans = useHasPermission("medication-plans:read");
  const canHydration = useHasPermission("hydration:read");
  const canManageHydration = useHasPermission("hydration:manage");
  const modulesKey = TABS.map((t) => (hasModule(t.module) ? 1 : 0)).join("");

  const visibleTabs = useMemo(() => {
    const perms = {
      "anamnez:read": canAnamnez,
      "tetkik:read": canTetkik,
      "treatments:read": canTreatments,
      "diet:read": canDiet,
      "medication-plans:read": canMedPlans,
      "hydration:read": canHydration,
    };
    return TABS.filter(
      (t, i) =>
        (!t.perm || perms[t.perm]) &&
        modulesKey[i] === "1" &&
        // Reçeteler tedaviden okur; tedavi modülü kapalıysa onu da gizle
        (t.key !== "prescriptions" || modulesKey[TABS.findIndex((x) => x.key === "treatments")] === "1")
    );
  }, [canAnamnez, canTetkik, canTreatments, canDiet, canMedPlans, canHydration, modulesKey]);

  // If the currently active tab is no longer visible (e.g. restored tab whose
  // permission was revoked), fall back to personal-info which is always available.
  useEffect(() => {
    if (!visibleTabs.find((t) => t.key === activeTab)) {
      setActiveTab("personal-info");
    }
  }, [visibleTabs, activeTab]);

  const contextValue = useMemo(
    () => ({
      mode: "session",
      examinationId: vm.examinationId,
      isReadOnly: vm.isReadOnly,
      registerAutoSave: vm.autoSaveRegistry?.register,
      registerFlush: vm.autoSaveRegistry?.registerFlush,
    }),
    [vm.examinationId, vm.isReadOnly, vm.autoSaveRegistry]
  );

  const goBack = () => navigate("/Randevular");

  if (vm.isLoading) {
    // Başlık şeridi + sekme satırı + düz satırlar (Hasta Detay iskeletiyle aynı dil)
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: COLORS.white }}>
        <Box sx={{ borderBottom: `1px solid ${COLORS.slate200}` }}>
          <Box sx={{ px: { xs: 2, md: 4 }, py: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={220} height={26} />
              <Skeleton variant="text" width={320} height={16} />
            </Box>
            <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={150} height={32} sx={{ borderRadius: 2 }} />
          </Box>
          <Box sx={{ px: { xs: 2, md: 4 }, display: "flex", gap: 3, height: 42, alignItems: "center" }}>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} variant="text" width={70 + (i % 3) * 14} height={18} />
            ))}
          </Box>
        </Box>
        {[0, 1, 2, 3, 4].map((k) => (
          <Box key={k} sx={{ px: { xs: 2, md: 4 }, py: 1.5, borderBottom: `1px solid ${COLORS.slate100}` }}>
            <Skeleton variant="text" width={`${30 + (k % 3) * 12}%`} height={20} />
            <Skeleton variant="text" width={`${18 + (k % 2) * 10}%`} height={16} />
          </Box>
        ))}
      </Box>
    );
  }

  if (vm.error || !vm.session) {
    return (
      <Box sx={{ height: "100%", bgcolor: COLORS.white }}>
        <EntityHeader onBack={goBack} title={terms.exam} />
        <Alert severity="error" sx={{ mx: { xs: 2, md: 4 }, my: 1.5 }}>
          {terms.exam} yüklenemedi:{" "}
          {String(vm.error?.data?.message || vm.error?.message || "Bilinmeyen hata")}
        </Alert>
      </Box>
    );
  }

  const examination = vm.session.examination;
  const patient = examination?.patient || vm.session.patient;
  const patientName = fullName(patient, terms);
  const canEdit = !vm.isReadOnly;

  const doctor = examination?.doctor || vm.session?.doctor;
  const doctorName = doctor ? staffFullName(doctor) : "";
  const doctorRoleLabel = staffRoleName(doctor) || terms.doctor;
  const appointment = examination?.appointment;
  const apptTypeLabel = (() => {
    const t = appointment?.appointmentType;
    if (t === "ilk_muayene") return terms.firstVisitTitle;
    if (t === "kontrol") return terms.followUp;
    return null;
  })();
  const patientInitials = `${((patient?.name || "")[0] || "").toUpperCase()}${((patient?.surname || "")[0] || "").toUpperCase()}`;

  // Alt satır: Hasta Detay ile aynı kalıp ("45 yaşında · Kadın · …") + muayene bilgisi
  const age = calcAge(patient?.birthDay);
  const subline = [
    age != null ? `${age} yaşında` : null,
    patient?.gender || null,
    patient?.tcNo ? `TC ${patient.tcNo}` : null,
    patient?.tellNo ? formatPhoneDisplay(patient.tellNo) : null,
    doctorName ? `${doctorRoleLabel}: ${doctorName}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const common = {
    mode: "session",
    patient,
    patientId: patient?._id,
    token: vm.token,
    canEdit,
    canManageHydration,
    registerAutoSave: vm.autoSaveRegistry.register,
    registerFlush: vm.autoSaveRegistry.registerFlush,
    onSaveStatusChange: vm.setSaveStatus,
    onRecordChanged: vm.refetchDetail,
  };

  const isFlat = FLAT_TABS.has(activeTab);

  return (
    <ExaminationProvider value={contextValue}>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: COLORS.slate50,
          overflow: "hidden",
        }}
      >
        <EntityHeader
          onBack={goBack}
          avatar={{ initials: patientInitials || undefined, alt: patientName }}
          title={patientName}
          subtitle={subline || undefined}
          chips={[
            apptTypeLabel ? (
              <Chip
                key="appt-type"
                label={apptTypeLabel}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  bgcolor: alpha(palette.primary, 0.08),
                  color: palette.primary,
                }}
              />
            ) : null,
          ]}
          actions={
            <>
              <SaveStatusIndicator status={vm.saveStatus} palette={palette} inline />
              <DurationTimer
                startedAt={examination.startedAt}
                // Süren muayeneyi başkası izliyorsa sayaç akmaya devam eder
                frozenMs={examination.status === "in_progress" ? null : examination.durationMs}
              />
              {!vm.isReadOnly && (
                <>
                  <ActionButton
                    variant="delete"
                    compact
                    tone="soft"
                    label="İptal"
                    onClick={() => setCancelOpen(true)}
                  />
                  <ActionButton
                    variant="confirm"
                    compact
                    palette={palette}
                    label={vm.isCompleting ? "Tamamlanıyor..." : terms.examComplete}
                    loading={vm.isCompleting}
                    onClick={vm.handleComplete}
                  />
                </>
              )}
            </>
          }
          tabs={visibleTabs.map((t) => ({ key: t.key, label: t.label }))}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <CompletionBanner
          status={examination.status}
          completedAt={examination.completedAt}
          cancelledAt={examination.cancelledAt}
          durationMs={examination.durationMs}
          cancelReason={examination.cancelReason}
        />

        {/* Başkasının süren muayenesi: neden düzenlenemediği açık olsun */}
        {vm.isReadOnly && examination.status === "in_progress" && (
          <StatusStrip
            icon={ViewOnlyIcon}
            tone={palette.primary}
            title="Yalnızca görüntüleme"
            detail={`Bu ${terms.examLower}yi ${staffFullName(examination.doctor) || `randevunun ${terms.doctorPossLower}`} yürütüyor; değişiklik yapılamaz.`}
          />
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            overflowX: "hidden",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            // Kartlı sekmede başlık şeridiyle aynı yatay hiza (design-principles §2)
            ...(isFlat ? { p: 0, bgcolor: COLORS.white } : CARD_PAGE_PAD),
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isFlat ? (
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {renderTab(activeTab, common, vm.session, vm.supportData, patient)}
            </Box>
          ) : (
            renderTab(activeTab, common, vm.session, vm.supportData, patient)
          )}
        </Box>
      </Box>

      <CancelDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={async (reason) => {
          await vm.handleCancel(reason);
          setCancelOpen(false);
        }}
        isCancelling={vm.isCancelling}
      />

      <Snackbar
        open={!!vm.completeError}
        autoHideDuration={6000}
        onClose={vm.clearCompleteError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={vm.clearCompleteError} sx={{ width: "100%" }}>
          {vm.completeError}
        </Alert>
      </Snackbar>
    </ExaminationProvider>
  );
}
